import { DestroyRef, Signal, WritableSignal, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subscription } from 'rxjs';
import { BugTargetChoice, NO_BUG_TARGET_CHOICE } from '../components/bug-target-picker/bug-target-choice';
import { LiveBugContext } from '../dashboard/testlauf-runner/live-bug-dialog/live-bug-dialog.component';
import { LiveBugError, liveBugError } from '../dashboard/testlauf-runner/live-bug-error';
import { ProjectIntegration } from '../models/project-integration.model';
import { CreateBugFromStepResultRequest, CreatedBug } from '../models/test-run.model';
import { StepBugLinkService } from './step-bug-link.service';
import { TestRunService } from './test-run.service';

export interface CaseResultBugDialog {
  caseResultId: string;
  context: LiveBugContext;
}

/** Der Bug am Durchlauf, wo es keinen fehlgeschlagenen Fall dafuer gibt. */
export interface ExecutionBugDialog {
  runId: string;
  executionId: string;
  context: LiveBugContext;
}

export type CaseResultBugRun = Pick<LiveBugContext, 'runKey' | 'executionNr' | 'pipeline'>;

/** {@code id} ist das Fall-Ergebnis bzw. der Durchlauf, an dem der Vorgang nun haengt. */
export interface CaseResultBugHandlers {
  created(id: string, key: string): void;
  alreadyLinked(): void;
}

/** Was beide Wege teilen: Ziele, Ziel-Wahl, Dialog, Absage und der Absprung zum Vorgang. */
interface BugFlow<D> {
  readonly canCreate: Signal<boolean>;
  readonly targets: Signal<ProjectIntegration[]>;
  readonly choice: WritableSignal<BugTargetChoice>;
  readonly dialog: Signal<D | null>;
  readonly submitting: Signal<boolean>;
  readonly error: Signal<LiveBugError | null>;
  close(): D | null;
  confirm(projectId: string, request: CreateBugFromStepResultRequest, handlers: CaseResultBugHandlers): void;
  urlFor(id: string, key: string): string | null;
}

/** Was der Server beim Anlegen anhaengt, solange der Dialog es zeigt. */
export interface BugPreviewState {
  status: 'loading' | 'ready' | 'failed';
  /** Die Fehlermeldung, wie der Server sie anhaengt; {@code null}, wo es keine gibt. */
  text: string | null;
}

export interface CaseResultBugFlow extends BugFlow<CaseResultBugDialog> {
  readonly preview: Signal<BugPreviewState>;
  /** Zeigt den Dialog sofort und laedt die Vorschau danach; deren Scheitern sperrt das Anlegen nicht. */
  open(projectId: string, caseResultId: string, context: LiveBugContext): void;
}

export interface ExecutionBugFlow extends BugFlow<ExecutionBugDialog> {
  /** Oeffnet den Dialog erst mit der Vorschau dessen, was der Server anhaengt; ohne sie trotzdem. */
  open(projectId: string, runId: string, executionId: string, context: LiveBugContext): void;
}

interface BugFlowCore<D> extends BugFlow<D> {
  show(dialog: D): void;
}

/** Wie ein Weg den Vorgang anlegt und unter welcher Kennung er dessen Absprung merkt. */
interface BugFlowWay<D> {
  id(dialog: D): string;
  create(projectId: string, dialog: D, request: CreateBugFromStepResultRequest): Observable<CreatedBug>;
}

function bugFlow<D>(way: BugFlowWay<D>, choice: WritableSignal<BugTargetChoice>): BugFlowCore<D> {
  const bugLinks = inject(StepBugLinkService);
  const destroyRef = inject(DestroyRef);

  const dialog = signal<D | null>(null);
  const submitting = signal(false);
  const error = signal<LiveBugError | null>(null);

  return {
    canCreate: bugLinks.canCreateIssue,
    targets: bugLinks.creationTargets,
    choice,
    dialog: dialog.asReadonly(),
    submitting: submitting.asReadonly(),
    error: error.asReadonly(),

    show(next) {
      if (dialog()) return;
      error.set(null);
      dialog.set(next);
    },

    close() {
      const closed = dialog();
      if (!closed || submitting()) return null;
      dialog.set(null);
      error.set(null);
      return closed;
    },

    confirm(projectId, request, handlers) {
      const current = dialog();
      if (!current || submitting()) return;
      const id = way.id(current);
      submitting.set(true);
      error.set(null);
      way
        .create(projectId, current, request)
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe({
          next: (bug) => {
            submitting.set(false);
            bugLinks.remember(id, bug.browseUrl);
            handlers.created(id, bug.key);
          },
          error: (failure: HttpErrorResponse) => {
            submitting.set(false);
            const message = liveBugError(failure, targetBoardUrl(bugLinks.creationTargets(), request.integrationId));
            error.set(message);
            if (message.alreadyLinked) handlers.alreadyLinked();
          },
        });
    },

    urlFor: (id, key) => bugLinks.urlFor(id, key),
  };
}

/**
 * Als Funktion im Injection-Kontext statt als Service: jede Ansicht haelt ihren eigenen
 * Dialog, teilt sich aber die Anbindungen und gemerkten Links des {@link StepBugLinkService}
 * ihrer Seite.
 */
export function caseResultBugFlow(): CaseResultBugFlow {
  const runService = inject(TestRunService);
  const destroyRef = inject(DestroyRef);
  const { show, ...core } = bugFlow<CaseResultBugDialog>(
    {
      id: (dialog) => dialog.caseResultId,
      create: (projectId, dialog, request) =>
        runService.createBugFromCaseResult(projectId, dialog.caseResultId, request),
    },
    signal(NO_BUG_TARGET_CHOICE),
  );
  const preview = signal<BugPreviewState>({ status: 'loading', text: null });
  let loading: Subscription | null = null;

  return {
    ...core,
    preview: preview.asReadonly(),

    open(projectId, caseResultId, context) {
      if (core.dialog()) return;
      show({ caseResultId, context });
      preview.set({ status: 'loading', text: null });
      loading = runService
        .previewBugFromCaseResult(projectId, caseResultId)
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe({
          next: ({ errorSection }) =>
            preview.set({ status: 'ready', text: errorSection?.trim() ? errorSection : null }),
          error: () => preview.set({ status: 'failed', text: null }),
        });
    },

    close() {
      const closed = core.close();
      if (closed) loading?.unsubscribe();
      return closed;
    },
  };
}

/**
 * Derselbe Ablauf fuer einen Bug am Durchlauf. Mit {@code choice} teilt er die Ziel-Wahl
 * einer anderen Anlage auf derselben Seite, statt dort noch einmal fragen zu muessen.
 */
export function executionBugFlow(
  choice: WritableSignal<BugTargetChoice> = signal(NO_BUG_TARGET_CHOICE),
): ExecutionBugFlow {
  const runService = inject(TestRunService);
  const destroyRef = inject(DestroyRef);
  const { show, ...core } = bugFlow<ExecutionBugDialog>(
    {
      id: (dialog) => dialog.executionId,
      create: (projectId, dialog, request) =>
        runService.createBugFromExecution(projectId, dialog.runId, dialog.executionId, request),
    },
    choice,
  );
  let loading = false;

  return {
    ...core,
    open(projectId, runId, executionId, context) {
      if (loading || core.dialog()) return;
      loading = true;
      const showWith = (anhang: string | null): void => {
        loading = false;
        const ausfuehrung = context.ausfuehrung ? { ...context.ausfuehrung, anhang } : undefined;
        show({ runId, executionId, context: ausfuehrung ? { ...context, ausfuehrung } : context });
      };
      runService
        .previewBugFromExecution(projectId, runId, executionId)
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe({
          next: (preview) => showWith(preview.errorSection ?? null),
          error: () => showWith(null),
        });
    },
  };
}

/** Ohne Wahl legt der Server den Vorgang in der ersten aktiven Verbindung an. */
function targetBoardUrl(targets: ProjectIntegration[], integrationId: string | undefined): string | null {
  const target = targets.find((candidate) => candidate.id === integrationId) ?? targets[0];
  return target?.boardUrl ?? null;
}
